import * as React from 'react';
import { ReportFieldDefinition } from "../../../../models";
import styled from 'styled-components';
import { Button } from '@mui/material';
import { TK } from '../../../../store/translations/translationKeys';
import { useTranslations } from '../../../../store/translations/hooks';

interface SignatureFieldProps {
    id: string,
    value?: string,
    fieldDefinition: ReportFieldDefinition,
    style?: React.CSSProperties,
    readOnly?: boolean,
    invalid?: boolean,
    onChange?: (value: string) => void,
}

const Container = styled('div')`
    display: flex;
    flex-direction: column;
    gap: var(--spacer-XS);
    width: min(32rem, 80vw);
    user-select: none;
    -webkit-user-select: none;
    -webkit-touch-callout: none;
`;

const CanvasContainer = styled('div')<{ $invalid?: boolean; $readOnly?: boolean }>`
    position: relative;
    border: var(--border-medium) solid ${props => props.$invalid ? 'var(--danger-color)' : 'var(--appLigthGrey)'};
    border-radius: var(--spacer-XXS);
    background-color: ${props => props.$readOnly ? 'var(--bg-light)' : '#ffffff'};
    cursor: ${props => props.$readOnly ? 'not-allowed' : 'crosshair'};
    overflow: hidden;
    touch-action: none;
`;

const Canvas = styled('canvas')`
    display: block;
    width: 100%;
    height: 100%;
    touch-action: none;
`;

const ButtonContainer = styled('div')`
    display: flex;
    gap: var(--spacer-XS);
`;

const configureContext = (context: CanvasRenderingContext2D) => {
    context.strokeStyle = '#000000';
    context.fillStyle = '#000000';
    context.lineWidth = 2;
    context.lineCap = 'round';
    context.lineJoin = 'round';
};

export const ReportSignatureField: React.FC<SignatureFieldProps> = ({
    id,
    value,
    style,
    invalid,
    readOnly,
    onChange,
}) => {
    const t = useTranslations();
    const canvasRef = React.useRef<HTMLCanvasElement>(null);
    const [hasSignature, setHasSignature] = React.useState(false);

    // Refs so native event listeners always see latest values without re-binding
    const readOnlyRef = React.useRef(readOnly);
    readOnlyRef.current = readOnly;
    const onChangeRef = React.useRef(onChange);
    onChangeRef.current = onChange;
    const hasSignatureRef = React.useRef(false);

    const isDrawingRef = React.useRef(false);
    const lastPointRef = React.useRef<{ x: number; y: number } | null>(null);
    const lastMidRef = React.useRef<{ x: number; y: number } | null>(null);
    const onChangeTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    // Initialize canvas size and load existing signature once on mount
    React.useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width * window.devicePixelRatio;
        canvas.height = rect.height * window.devicePixelRatio;
        const context = canvas.getContext('2d')!;
        context.scale(window.devicePixelRatio, window.devicePixelRatio);
        configureContext(context);

        if (value) {
            const image = new Image();
            image.onload = () => {
                context.drawImage(image, 0, 0, rect.width, rect.height);
                hasSignatureRef.current = true;
                setHasSignature(true);
            };
            image.src = value;
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Resize canvas on orientation/layout changes, preserving drawing content
    React.useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const observer = new ResizeObserver(() => {
            if (isDrawingRef.current) return;

            const context = canvas.getContext('2d');
            if (!context) return;

            const dataUrl = hasSignatureRef.current ? canvas.toDataURL('image/png') : null;
            const rect = canvas.getBoundingClientRect();
            canvas.width = rect.width * window.devicePixelRatio;
            canvas.height = rect.height * window.devicePixelRatio;
            context.scale(window.devicePixelRatio, window.devicePixelRatio);
            configureContext(context);

            if (dataUrl) {
                const image = new Image();
                image.onload = () => context.drawImage(image, 0, 0, rect.width, rect.height);
                image.src = dataUrl;
            }
        });

        observer.observe(canvas);
        return () => observer.disconnect();
    }, []);

    // Attach input event listeners directly on the canvas DOM node.
    // Touch events are used as primary input because on iOS Safari with Apple Pencil,
    // pointer events go through a ~500ms gesture-detection pipeline (double-tap detection)
    // that touch events bypass entirely. Calling preventDefault() in touchstart suppresses
    // the corresponding pointer events so nothing fires twice.
    React.useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const getCoords = (clientX: number, clientY: number) => {
            const rect = canvas.getBoundingClientRect();
            return { x: clientX - rect.left, y: clientY - rect.top };
        };

        const notifyChange = () => {
            if (onChangeTimerRef.current) clearTimeout(onChangeTimerRef.current);
            onChangeTimerRef.current = setTimeout(() => {
                if (onChangeRef.current) {
                    onChangeRef.current(canvas.toDataURL('image/png'));
                }
            }, 0);
        };

        const beginStroke = (x: number, y: number) => {
            const ctx = canvas.getContext('2d');
            if (!ctx) return;
            isDrawingRef.current = true;
            lastPointRef.current = { x, y };
            lastMidRef.current = { x, y };
            ctx.beginPath();
            ctx.arc(x, y, ctx.lineWidth / 2, 0, Math.PI * 2);
            ctx.fill();
            ctx.beginPath();
            ctx.moveTo(x, y);
        };

        const continueStroke = (x: number, y: number) => {
            const ctx = canvas.getContext('2d');
            if (!ctx) return;
            const last = lastPointRef.current;
            const lastMid = lastMidRef.current;
            if (last && lastMid) {
                const midX = (last.x + x) / 2;
                const midY = (last.y + y) / 2;
                ctx.beginPath();
                ctx.moveTo(lastMid.x, lastMid.y);
                ctx.quadraticCurveTo(last.x, last.y, midX, midY);
                ctx.stroke();
                lastMidRef.current = { x: midX, y: midY };
            }
            lastPointRef.current = { x, y };
        };

        const endStroke = () => {
            if (!isDrawingRef.current) return;
            isDrawingRef.current = false;
            lastPointRef.current = null;
            lastMidRef.current = null;
            if (!hasSignatureRef.current) {
                hasSignatureRef.current = true;
                setHasSignature(true);
            }
            notifyChange();
        };

        // ── Touch events (Apple Pencil + finger on iOS) ──────────────────────
        // Fire immediately — no 500ms gesture detection delay.
        const onTouchStart = (e: TouchEvent) => {
            if (readOnlyRef.current) return;
            e.preventDefault(); // also suppresses the matching pointer events
            const t = e.changedTouches[0];
            const { x, y } = getCoords(t.clientX, t.clientY);
            beginStroke(x, y);
        };

        const onTouchMove = (e: TouchEvent) => {
            if (!isDrawingRef.current || readOnlyRef.current) return;
            e.preventDefault();
            const t = e.changedTouches[0];
            const { x, y } = getCoords(t.clientX, t.clientY);
            continueStroke(x, y);
        };

        const onTouchEnd = (e: TouchEvent) => {
            e.preventDefault();
            endStroke();
        };

        // ── Pointer events (mouse only) ───────────────────────────────────────
        // Pen/touch are handled above; skip them here to avoid double-firing
        // on devices where both touch and pointer events fire.
        const onPointerDown = (e: PointerEvent) => {
            if (e.pointerType !== 'mouse') return;
            if (readOnlyRef.current) return;
            e.preventDefault();
            const { x, y } = getCoords(e.clientX, e.clientY);
            beginStroke(x, y);
        };

        const onPointerMove = (e: PointerEvent) => {
            if (e.pointerType !== 'mouse') return;
            if (!isDrawingRef.current || readOnlyRef.current) return;
            e.preventDefault();
            const { x, y } = getCoords(e.clientX, e.clientY);
            continueStroke(x, y);
        };

        const onPointerUp = (e: PointerEvent) => {
            if (e.pointerType !== 'mouse') return;
            endStroke();
        };

        const onContextMenu = (e: Event) => e.preventDefault();

        canvas.addEventListener('touchstart', onTouchStart, { passive: false });
        canvas.addEventListener('touchmove', onTouchMove, { passive: false });
        canvas.addEventListener('touchend', onTouchEnd, { passive: false });
        canvas.addEventListener('touchcancel', onTouchEnd, { passive: false });
        canvas.addEventListener('pointerdown', onPointerDown, { passive: false });
        canvas.addEventListener('pointermove', onPointerMove, { passive: false });
        canvas.addEventListener('pointerup', onPointerUp);
        canvas.addEventListener('pointerleave', onPointerUp);
        canvas.addEventListener('contextmenu', onContextMenu);

        return () => {
            canvas.removeEventListener('touchstart', onTouchStart);
            canvas.removeEventListener('touchmove', onTouchMove);
            canvas.removeEventListener('touchend', onTouchEnd);
            canvas.removeEventListener('touchcancel', onTouchEnd);
            canvas.removeEventListener('pointerdown', onPointerDown);
            canvas.removeEventListener('pointermove', onPointerMove);
            canvas.removeEventListener('pointerup', onPointerUp);
            canvas.removeEventListener('pointerleave', onPointerUp);
            canvas.removeEventListener('contextmenu', onContextMenu);
        };
    }, []);

    const clearSignature = () => {
        if (readOnly) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const context = canvas.getContext('2d');
        if (!context) return;

        const width = canvas.width;
        canvas.width = width;
        context.scale(window.devicePixelRatio, window.devicePixelRatio);
        configureContext(context);

        hasSignatureRef.current = false;
        setHasSignature(false);

        if (onChange) onChange('');
    };

    return (
        <Container style={style}>
            <CanvasContainer $invalid={invalid} $readOnly={readOnly}>
                <Canvas
                    ref={canvasRef}
                    id={id}
                    style={{ width: '100%', minHeight: '5rem' }}
                />
            </CanvasContainer>
            {!readOnly && (
                <ButtonContainer>
                    <Button
                        variant="outlined"
                        size="small"
                        color="secondary"
                        onClick={clearSignature}
                        disabled={!hasSignature}
                    >
                        {t(TK.clear)}
                    </Button>
                </ButtonContainer>
            )}
        </Container>
    );
};
